import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import BrandNameModel from './BrandNameModel';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface BrandNames {
  brand_id: number;
  brand_name: string;
  brand_status: number;
  }

const BrandNames: React.FC = () => {
  const [brandnames, setBrandNames] = useState<BrandNames[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<BrandNames | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchBrandNames = async () => {
    try {
      const data = await apiRequest<BrandNames[]>({
        method: 'get',
        url: 'brand',
      });
      setBrandNames(data);
    } catch (error) {
      showError('Failed to fetch brandnames');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchBrandNames();
  }, []);

  const handleEditClick = (record: BrandNames) => {
    setIsEditMode(true);
    setInitialValues({
      brand_id: record.brand_id,
      brand_name: record.brand_name,
      brand_status: record.brand_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchBrandNames();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (brand_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-brand/${brand_id}`,
      });
      showSuccess('brand-name deleted');
      fetchBrandNames();
    } catch {
      showError('Failed to delete brand-name');
    }
  };

  const handleStatusToggle = async (BrandNameId: number) => {
    setBrandNames((prev) =>
      prev.map((item) =>
        item.brand_id === BrandNameId
          ? { ...item, brand_status: item.brand_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-brand/${BrandNameId}`,
      });
      showSuccess('Brand name status updated');
    } catch (error) {
      showError('Failed to update status');
      setBrandNames((prev) =>
        prev.map((item) =>
          item.brand_id === BrandNameId
            ? { ...item, brand_status: item.brand_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<BrandNames>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<BrandNames> = [
    {
      title: 'Brand Name',
      dataIndex: 'brand_name',
      key: 'brand_name',
      filters: Array.from(new Set(brandnames.map((s) => s.brand_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.brand_name || null,
      onFilter: (value, record) => record.brand_name.includes(value as string),
      sorter: (a, b) => a.brand_name.localeCompare(b.brand_name),
      sortOrder: sortedInfo.columnKey === 'brand_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.brand_name}</div>,
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: BrandNames) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.brand_status}
                onChange={() => handleStatusToggle(record.brand_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this brand name?"
              onConfirm={() => handleDelete(record.brand_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View BrandNames</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Brand Name
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<BrandNames>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="brand_id"
          dataSource={brandnames.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No brand name found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, brandnames.length)} of{' '}
                {brandnames.length}
              </span>
              <Pagination
                {...pagination}
                total={brandnames.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <BrandNameModel
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default BrandNames;